import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { fetchOrgProjectsOverview } from "@/stores/organizations/repository";

/**
 * All projects the caller can see across an org, with building info and
 * per-project counts — the /projects landing data. Defaults to the ambient
 * org context, gated on org readiness per the CLAUDE.md rule (never fetch
 * with orgId undefined). Pass an explicit `orgId` when the surface is scoped
 * to a specific org regardless of the header switcher (e.g. the building
 * dashboard uses the building's own org_id, so a deep link into a building
 * from another org still gets its counts). Both cases wait for `isReady` —
 * it is the auth-settled signal, so firing before it can send the RPC
 * unauthenticated on a fresh deep link.
 */
export function useOrgProjectsOverview(explicitOrgId?: string) {
  const { currentOrganization, isReady } = useOrganizationContext();
  const orgId = explicitOrgId ?? currentOrganization?.id;

  const query = useQuery({
    queryKey: ["org-projects-overview", orgId],
    queryFn: () => fetchOrgProjectsOverview(orgId as string),
    enabled: !!orgId && isReady,
    // This route sits outside BuildingLayout, so the documents realtime bridge
    // does not reach it. react-query's refetchOnWindowFocus is already on by
    // default; the global 5-minute staleTime is what was suppressing it, and
    // 60s still left per-project document counts lagging every upload.
    staleTime: 30_000,
  });

  // Consumers degrade gracefully on error (counts render as "—", project
  // browsing turns off), so report here — once for every surface — instead of
  // failing silently in production.
  useEffect(() => {
    if (query.error) {
      console.error("get_org_projects_overview failed:", query.error);
      Sentry.captureException(query.error, {
        tags: { feature: "org-projects-overview" },
        extra: { orgId },
      });
    }
  }, [query.error, orgId]);

  return {
    rows: query.data ?? [],
    isLoading: !isReady || query.isPending,
    error: query.error,
    refetch: query.refetch,
  };
}
